import type { ReactNode } from 'react';
import TarotCard from './TarotCard';
import CardBackDesign from './CardBackDesign';
import type { Persona, Theme } from '../lib/personas';

const STAGE_SRC: Record<Persona, string> = {
  luna: '/image2/readers/generated/stage-luna.jpg',
  orion: '/image2/readers/generated/stage-orion.jpg',
  sage: '/image2/readers/generated/stage-sage.jpg',
};

interface ReaderStageProps {
  persona: Persona;
  theme: Theme;
  cards: Array<{ slug: string; name: string; positionLabel: string }>;
  children?: ReactNode;
}

export default function ReaderStage({
  persona,
  theme,
  cards,
  children,
}: ReaderStageProps) {
  const hasCards = cards.length === 3;

  return (
    <section
      className={['reader-stage', children ? 'reader-stage--interactive' : ''].join(' ')}
      data-theme={theme}
      data-persona={persona}
      aria-label="占卜師與牌桌"
    >
      <img
        className="reader-stage__scene"
        src={STAGE_SRC[persona]}
        alt=""
        draggable={false}
      />

      <div className="reader-stage__place" aria-hidden="true">
        <div className="reader-stage__wall" />
        <div className="reader-stage__window" />
        <div className="reader-stage__shelves">
          <span />
          <span />
          <span />
        </div>
        <div className="reader-stage__lamp" />
        <div className="reader-stage__cat">
          <span className="reader-stage__cat-body" />
          <span className="reader-stage__cat-tail" />
        </div>
      </div>

      <div className="reader-stage__table" aria-hidden="true">
        <div className="reader-stage__cloth" />
      </div>

      {children ? (
        <div className="reader-stage__console">{children}</div>
      ) : (
        <div className="reader-stage__spread" data-testid="spread-dock">
          <div className="reader-stage__spread-copy">
            <p>Past · Present · Future</p>
            <h2>三張牌展牌</h2>
          </div>
          <div className="reader-stage__cards">
            {(hasCards ? cards : ['過去', '現在', '未來'].map((positionLabel) => ({
              slug: '',
              name: '',
              positionLabel,
            }))).map((card, i) => (
              <div
                key={`${card.positionLabel}-${card.slug || i}`}
                className="reader-stage__card-slot"
                data-testid={hasCards ? `spread-position-${i}` : undefined}
              >
                {hasCards ? (
                  <>
                    <TarotCard
                      slug={card.slug}
                      theme={theme}
                      revealed
                      flipMs={360}
                      showTitle={false}
                      className="reader-stage__tarot"
                    />
                    <p>
                      <span>{card.positionLabel}</span>
                      {card.name && <em>{card.name}</em>}
                    </p>
                  </>
                ) : (
                  <>
                    <div className="reader-stage__card-back">
                      <CardBackDesign theme={theme} selected={i === 1} />
                    </div>
                    <p>
                      <span>{card.positionLabel}</span>
                    </p>
                  </>
                )}
              </div>
            ))}
          </div>
          <div className="reader-stage__status">
            {hasCards ? (
              <p data-testid="reveal-status">三張牌已翻開 · 下面只讀解讀</p>
            ) : (
              <p>牌會固定在桌面，對話在下方繼續</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
